import React, { useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import {
  Download,
  Upload,
  AlertCircle,
  AlertTriangle,
  Check,
  X,
  FileJson,
  FileSpreadsheet
} from 'lucide-react';

type EntityType = 'symptom_groups' | 'symptoms' | 'approaches' | 'questions' | 'influence_rules' | 'all';

interface ExportOptions {
  format: 'json' | 'csv';
  entityType: EntityType;
  includeInactive: boolean;
}

export function ImportExport() {
  const { profile } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [exportOptions, setExportOptions] = useState<ExportOptions>({
    format: 'json',
    entityType: 'all',
    includeInactive: false
  });

  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [importPreview, setImportPreview] = useState<{ data: any; filename: string } | null>(null);
  const [showImportConfirm, setShowImportConfirm] = useState(false);

  // Tables that have the is_active column
  const tablesWithIsActive: EntityType[] = ['symptom_groups', 'symptoms', 'approaches', 'questions'];

  const handleExport = async () => {
    setLoading(true);
    setMessage(null);

    try {
      let data: any;

      if (exportOptions.entityType === 'all') {
        const [groups, symptoms, approaches, questions, rules] = await Promise.all([
          supabase.from('symptom_groups').select('*').order('order_index'),
          supabase.from('symptoms').select('*').order('order_index'),
          supabase.from('approaches').select('*').order('number'),
          supabase.from('questions').select('*').order('order_index'),
          supabase.from('influence_rules').select('*')
        ]);

        // Check for errors in any query
        const errors = [
          groups.error && `symptom_groups: ${groups.error.message}`,
          symptoms.error && `symptoms: ${symptoms.error.message}`,
          approaches.error && `approaches: ${approaches.error.message}`,
          questions.error && `questions: ${questions.error.message}`,
          rules.error && `influence_rules: ${rules.error.message}`
        ].filter(Boolean);

        if (errors.length > 0) {
          setMessage({ type: 'error', text: `Feil ved henting: ${errors.join(', ')}` });
          setLoading(false);
          return;
        }

        data = {
          symptom_groups: groups.data || [],
          symptoms: symptoms.data || [],
          approaches: approaches.data || [],
          questions: questions.data || [],
          influence_rules: rules.data || []
        };
      } else {
        const query = supabase.from(exportOptions.entityType).select('*');

        // Only filter by is_active on tables that have the column
        if (!exportOptions.includeInactive && tablesWithIsActive.includes(exportOptions.entityType)) {
          query.eq('is_active', true);
        }

        if (exportOptions.entityType === 'symptom_groups' || exportOptions.entityType === 'symptoms') {
          query.order('order_index');
        } else if (exportOptions.entityType === 'approaches') {
          query.order('number');
        }

        const result = await query;
        if (result.error) {
          setMessage({ type: 'error', text: `Feil: ${result.error.message}` });
          setLoading(false);
          return;
        }
        data = result.data || [];
      }

      const dateStr = new Date().toISOString().split('T')[0];

      const toCsv = (rows: any[]): string => {
        if (rows.length === 0) return '';
        const headers = Object.keys(rows[0]);
        const escapeCsv = (val: any) => {
          if (val === null || val === undefined) return '';
          const str = typeof val === 'object' ? JSON.stringify(val) : String(val);
          if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            return `"${str.replace(/"/g, '""')}"`;
          }
          return str;
        };
        return [
          headers.join(','),
          ...rows.map(row => headers.map(h => escapeCsv(row[h])).join(','))
        ].join('\n');
      };

      const downloadFile = (content: string, filename: string, mimeType: string) => {
        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      };

      if (exportOptions.format === 'json') {
        downloadFile(
          JSON.stringify(data, null, 2),
          `helsenokkelen-backup-${exportOptions.entityType}-${dateStr}.json`,
          'application/json'
        );
      } else if (exportOptions.entityType === 'all') {
        // Download one CSV per table
        for (const [tableName, rows] of Object.entries(data as Record<string, any[]>)) {
          if ((rows as any[]).length > 0) {
            downloadFile(toCsv(rows as any[]), `helsenokkelen-${tableName}-${dateStr}.csv`, 'text/csv');
          }
        }
      } else {
        downloadFile(
          toCsv(data as any[]),
          `helsenokkelen-${exportOptions.entityType}-${dateStr}.csv`,
          'text/csv'
        );
      }

      const entityLabels: Record<EntityType, string> = {
        all: 'alle data',
        symptom_groups: 'symptomgrupper',
        symptoms: 'symptomer',
        approaches: 'tilnærminger',
        questions: 'spørsmål',
        influence_rules: 'påvirkningsregler'
      };

      setMessage({ type: 'success', text: `Eksporterte ${entityLabels[exportOptions.entityType]}` });

      // Audit log - non-blocking, don't let failures affect export
      supabase.from('audit_logs').insert({
        user_id: profile?.id,
        action: 'data_exported',
        entity_type: 'system_settings',
        details: {
          exported_type: exportOptions.entityType,
          format: exportOptions.format,
          record_count: Array.isArray(data) ? data.length : Object.keys(data).length
        }
      }).then(() => {});

    } catch (error: any) {
      setMessage({ type: 'error', text: `Kunne ikke eksportere data: ${error?.message || 'Ukjent feil'}` });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setMessage(null);
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const parsed = JSON.parse(event.target?.result as string);
        setImportPreview({ data: parsed, filename: file.name });
      } catch {
        setMessage({ type: 'error', text: 'Ugyldig JSON-fil. Sørg for at filen er en gyldig backup-fil.' });
      }
    };
    reader.readAsText(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const getImportSummary = (data: any): { table: string; count: number }[] => {
    const validTables = ['symptom_groups', 'symptoms', 'approaches', 'questions', 'influence_rules'];
    if (Array.isArray(data)) {
      return [{ table: 'ukjent', count: data.length }];
    }
    return Object.entries(data)
      .filter(([key]) => validTables.includes(key))
      .map(([key, val]) => ({ table: key, count: Array.isArray(val) ? val.length : 0 }))
      .filter(item => item.count > 0);
  };

  const tableLabels: Record<string, string> = {
    symptom_groups: 'Symptomgrupper',
    symptoms: 'Symptomer',
    approaches: 'Tilnærminger',
    questions: 'Spørsmål',
    influence_rules: 'Påvirkningsregler',
  };

  const handleImport = async () => {
    if (!importPreview) return;
    setImporting(true);
    setMessage(null);

    try {
      const data = importPreview.data;
      const results: string[] = [];
      const errors: string[] = [];

      const upsertTable = async (tableName: string, rows: any[]) => {
        if (!rows || rows.length === 0) return;
        const { error } = await supabase.from(tableName).upsert(rows, { onConflict: 'id' });
        if (error) {
          errors.push(`${tableLabels[tableName] || tableName}: ${error.message}`);
        } else {
          results.push(`${tableLabels[tableName] || tableName}: ${rows.length} rader`);
        }
      };

      const importOrder = ['symptom_groups', 'symptoms', 'approaches', 'questions', 'influence_rules'];

      if (typeof data === 'object' && !Array.isArray(data)) {
        for (const table of importOrder) {
          if (data[table] && Array.isArray(data[table])) {
            await upsertTable(table, data[table]);
          }
        }
      }

      if (errors.length > 0) {
        setMessage({ type: 'error', text: `Import delvis feilet: ${errors.join('; ')}` });
      } else if (results.length > 0) {
        setMessage({ type: 'success', text: `Importert: ${results.join(', ')}` });
      } else {
        setMessage({ type: 'error', text: 'Ingen gjenkjennelige data funnet i filen.' });
      }

      supabase.from('audit_logs').insert({
        user_id: profile?.id,
        action: 'data_imported',
        entity_type: 'system_settings',
        details: {
          filename: importPreview.filename,
          tables: results,
          errors: errors,
        }
      }).then(() => {});

      setImportPreview(null);
      setShowImportConfirm(false);
    } catch (error: any) {
      setMessage({ type: 'error', text: `Import feilet: ${error?.message || 'Ukjent feil'}` });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Import / Eksport</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Sikkerhetskopier systemdata eller gjenopprett fra backup
        </p>
      </div>

      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-2 ${
          message.type === 'success' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
          'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400'
        }`}>
          {message.type === 'success' ? <Check className="h-5 w-5" /> : <X className="h-5 w-5" />}
          {message.text}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Export */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border dark:border-gray-700 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Download className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Eksporter</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Data å eksportere
              </label>
              <select
                value={exportOptions.entityType}
                onChange={(e) => setExportOptions({
                  ...exportOptions,
                  entityType: e.target.value as EntityType
                })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Alle data</option>
                <option value="symptom_groups">Symptomgrupper</option>
                <option value="symptoms">Symptomer</option>
                <option value="approaches">Tilnærminger</option>
                <option value="questions">Spørsmål</option>
                <option value="influence_rules">Påvirkningsregler</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Format
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    value="json"
                    checked={exportOptions.format === 'json'}
                    onChange={() => setExportOptions({
                      ...exportOptions,
                      format: 'json'
                    })}
                    className="text-blue-600 dark:text-blue-400 focus:ring-blue-500"
                  />
                  <FileJson className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">JSON</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    value="csv"
                    checked={exportOptions.format === 'csv'}
                    onChange={() => setExportOptions({
                      ...exportOptions,
                      format: 'csv'
                    })}
                    className="text-blue-600 dark:text-blue-400 focus:ring-blue-500"
                  />
                  <FileSpreadsheet className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">CSV</span>
                </label>
              </div>
              {exportOptions.entityType === 'all' && exportOptions.format === 'csv' && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Laster ned én CSV-fil per tabell (5 filer)
                </p>
              )}
            </div>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={exportOptions.includeInactive}
                onChange={(e) => setExportOptions({
                  ...exportOptions,
                  includeInactive: e.target.checked
                })}
                className="rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Inkluder inaktive poster</span>
            </label>

            <button
              onClick={handleExport}
              disabled={loading}
              className="w-full px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 disabled:bg-gray-400 dark:disabled:bg-gray-600 flex items-center justify-center gap-2"
            >
              <Download className="h-4 w-4" />
              {loading ? 'Eksporterer...' : 'Last ned backup'}
            </button>

            <div className="p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-md">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5" />
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  Filen inneholder alt klinisk innhold og kan brukes til å gjenopprette data ved behov.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Import */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border dark:border-gray-700 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Upload className="h-5 w-5 text-green-600 dark:text-green-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Importer</h2>
          </div>

          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Gjenopprett data fra en tidligere eksportert JSON-backup.
            </p>

            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              className="hidden"
            />

            {!importPreview ? (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full px-4 py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-green-500 dark:hover:border-green-400 transition-colors flex flex-col items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400"
              >
                <Upload className="h-8 w-8" />
                <span className="text-sm font-medium">Velg JSON-fil</span>
              </button>
            ) : (
              <div className="space-y-3">
                <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                    {importPreview.filename}
                  </p>
                  <div className="space-y-1">
                    {getImportSummary(importPreview.data).map((item) => (
                      <div key={item.table} className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">
                          {tableLabels[item.table] || item.table}
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {item.count} rader
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {!showImportConfirm ? (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowImportConfirm(true)}
                      className="flex-1 px-4 py-2 bg-green-600 dark:bg-green-500 text-white rounded-md hover:bg-green-700 dark:hover:bg-green-600 flex items-center justify-center gap-2"
                    >
                      <Upload className="h-4 w-4" />
                      Importer data
                    </button>
                    <button
                      onClick={() => setImportPreview(null)}
                      className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500"
                    >
                      Avbryt
                    </button>
                  </div>
                ) : (
                  <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg space-y-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-yellow-800 dark:text-yellow-200">
                        Eksisterende data med samme ID vil bli overskrevet. Er du sikker?
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleImport}
                        disabled={importing}
                        className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:bg-gray-400 flex items-center justify-center gap-2"
                      >
                        {importing ? 'Importerer...' : 'Ja, importer'}
                      </button>
                      <button
                        onClick={() => { setShowImportConfirm(false); setImportPreview(null); }}
                        className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500"
                      >
                        Avbryt
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-md">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5" />
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  Kun JSON-filer eksportert fra Helsenøkkelen støttes. Data med eksisterende ID oppdateres (upsert).
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
